import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { appendSuperuserAudit } from './superuserAuditService';
import { IMPERSONATION_TTL_MS } from '../constants/impersonation';

/**
 * @param {string|null|undefined} tenantId - target tenant doc id, or null/undefined to stop impersonating.
 */
export async function setImpersonatingTenantId(tenantId) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not signed in');
  const next = tenantId && String(tenantId).trim() ? String(tenantId).trim() : null;
  await updateDoc(doc(db, 'users', uid), {
    impersonatingTenantId: next,
    impersonationStartedAt: next ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
  await appendSuperuserAudit({
    action: next ? 'impersonation_start' : 'impersonation_stop',
    tenantId: next,
  });
}

/**
 * Phase 6: set `impersonationStartedAt` if missing (migration), or clear impersonation when past TTL.
 * @returns {Promise<{ changed: boolean }>}
 */
export async function ensureImpersonationWithinTtl(profile) {
  const uid = auth.currentUser?.uid;
  const role = String(profile?.role || '').toLowerCase().trim();
  if (!uid || role !== 'superuser' || !profile?.impersonatingTenantId) {
    return { changed: false };
  }
  const started = profile.impersonationStartedAt;
  if (!started) {
    await updateDoc(doc(db, 'users', uid), {
      impersonationStartedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { changed: true };
  }
  const ms = typeof started.toMillis === 'function' ? started.toMillis() : new Date(started).getTime();
  if (Number.isFinite(ms) && Date.now() - ms > IMPERSONATION_TTL_MS) {
    await setImpersonatingTenantId(null);
    return { changed: true };
  }
  return { changed: false };
}
